/* Dark mode for Ghost's search modal.

   Sodo Search (the {{ghost_head}} search app) ships a light-only UI: the panel
   is hardcoded `bg-white` and the backdrop is a transparent blur, so on a dark
   page it renders as a pale card behind a grey wash. It exposes no colour
   option, but it mounts into a same-origin iframe under #sodo-search-root, so
   the theme can style it directly.

   The stylesheet is injected once the frame exists and refreshed whenever the
   scheme changes. Selectors target Sodo's utility classes, so a future release
   could rename them — hence everything here is additive: if a rule stops
   matching, the modal degrades to its stock light appearance rather than
   breaking. */
(function () {
    'use strict';

    var ROOT_ID = 'sodo-search-root';
    var STYLE_ID = 'es-search-scheme';

    /* Sodo's stock UI is designed for a light page, so it only needs
       overriding in dark mode. */
    function isDark() {
        var root = document.documentElement;
        if (root.classList.contains('es-dark')) {
            return true;
        }
        if (root.classList.contains('es-light')) {
            return false;
        }
        return window.matchMedia
            && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    function css() {
        if (!isDark()) {
            return '';
        }
        var s = getComputedStyle(document.documentElement);
        var bg = s.getPropertyValue('--es-bg').trim() || '#171717';
        var fg = s.getPropertyValue('--es-fg').trim() || '#f4f2ed';
        var rule = s.getPropertyValue('--es-rule').trim() || 'rgba(244,242,237,0.18)';
        var acid = s.getPropertyValue('--es-acid').trim() || '#d8ff36';
        var mono = s.getPropertyValue('--es-mono').trim() || 'monospace';
        var sans = s.getPropertyValue('--es-sans').trim() || 'sans-serif';

        return [
            /* Panel and any nested surfaces */
            '.bg-white{background-color:' + bg + '!important;color:' + fg + '!important;',
            'border:1px solid ' + rule + '!important;border-radius:0!important;box-shadow:none!important}',

            /* Backdrop. Two things fight a dark page here: Sodo paints a
               Tailwind gradient via background-image, which covers any
               background-color, and its backdrop-filter blur lifts a dark
               page toward grey. Replace the gradient and dim hard enough that
               the blur reads as depth rather than a wash. */
            '[class*="backdrop-blur"]{background-color:rgba(0,0,0,0.72)!important;',
            'background-image:none!important;backdrop-filter:blur(8px)!important;',
            '-webkit-backdrop-filter:blur(8px)!important}',

            /* Search field */
            'input{background:transparent!important;color:' + fg + '!important;',
            'font-family:' + sans + '!important}',
            'input::placeholder{color:' + fg + '!important;opacity:0.55!important}',

            /* Result rows and dividers */
            '[class*="border-"]{border-color:' + rule + '!important}',
            '[class*="hover:bg-"]:hover,[class*="bg-neutral"],[class*="bg-gray"]',
            '{background-color:rgba(216,255,54,0.12)!important}',

            /* Muted text: Sodo's greys are tuned for white */
            '[class*="text-neutral"],[class*="text-gray"],[class*="text-slate"]',
            '{color:' + fg + '!important;opacity:0.62}',

            /* Section labels match the theme's mono labels */
            '[class*="uppercase"]{font-family:' + mono + '!important;letter-spacing:0.14em!important;',
            'color:' + acid + '!important;opacity:1!important}',

            /* Keyboard focus stays visible on the dark panel */
            '*:focus-visible{outline:2px solid ' + acid + '!important;outline-offset:2px!important}'
        ].join('');
    }

    function paint(doc) {
        if (!doc || !doc.head) {
            return;
        }
        var style = doc.getElementById(STYLE_ID);
        if (!style) {
            style = doc.createElement('style');
            style.id = STYLE_ID;
            doc.head.appendChild(style);
        }
        style.textContent = css();
    }

    function frameDoc() {
        var root = document.getElementById(ROOT_ID);
        var frame = root && root.querySelector('iframe');
        try {
            return frame && frame.contentDocument;
        } catch (e) {
            /* cross-origin (should not happen for Sodo) — leave it stock */
            return null;
        }
    }

    function sync() {
        paint(frameDoc());
    }

    /* Sodo mounts asynchronously and re-renders its frame contents, so watch
       for the frame appearing and for its document being replaced. */
    var observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {childList: true, subtree: true});

    /* Re-theme when the visitor flips the scheme */
    new MutationObserver(sync).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
    });

    sync();
}());
